import React, { useMemo, useState } from "react";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import { supportedAssets } from "../data/supportedCryptoAssets";
import card from "@material-tailwind/react/theme/components/card";

const networkOptions = [
  "All",
  "Bitcoin",
  "Ethereum",
  "BNB SmartChain",
  "Ripple",
  "Kaia",
  "Tron",
  "Solana",
  "Polygon"
];

const allowedNetworks = networkOptions.filter((network) => network !== "All");

const IndexCryptoList = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeNetwork, setActiveNetwork] = useState("All");
  const visibleAssets = supportedAssets.filter((asset) => allowedNetworks.includes(asset.network));
  const assetsCount = visibleAssets.length;

  const filteredAssets = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return visibleAssets.filter((asset) => {
      const matchesNetwork = activeNetwork === "All" || asset.network === activeNetwork;
      const matchesQuery =
        query.length === 0 ||
        asset.name.toLowerCase().includes(query) ||
        asset.symbol.toLowerCase().includes(query) ||
        asset.network.toLowerCase().includes(query);
      return matchesNetwork && matchesQuery;
    });
  }, [searchQuery, activeNetwork]);

  const styles = {
    page: {
      minHeight: "100vh",
      backgroundColor: "#F8F7F6",
      color: "#1F2937",
    },
    main: {
      maxWidth: "1200px",
      margin: "0 auto",
      padding: "90px 20px 40px",
    },
    hero: {
      textAlign: "center",
      marginBottom: "40px",
    },
    title: {
      fontSize: "2.6rem",
      fontWeight: 700,
      marginBottom: "18px",
      lineHeight: 1.1,
    },
    subtitle: {
      fontSize: "1rem",
      color: "#4B5563",
      maxWidth: "760px",
      margin: "0 auto",
      lineHeight: 1.8,
    },
    searchBar: {
      width: "100%",
      maxWidth: "640px",
      margin: "24px auto",
      display: "flex",
      gap: "12px",
      flexWrap: "wrap",
      justifyContent: "center",
    },
    input: {
      flex: 1,
      minWidth: "220px",
      padding: "14px 16px",
      borderRadius: "12px",
      border: "1px solid #D1D5DB",
      backgroundColor: "#FFFFFF",
      color: "#111827",
      fontSize: "1rem",
    },
    chips: {
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      gap: "10px",
      margin: "24px auto",
      maxWidth: "880px",
    },
    chip: {
      padding: "10px 16px",
      borderRadius: "999px",
      border: "1px solid #D1D5DB",
      backgroundColor: "#FFFFFF",
      color: "#374151",
      cursor: "pointer",
      fontSize: "0.95rem",
      whiteSpace: "nowrap",
      transition: "all 0.2s ease",
    },
    chipActive: {
      backgroundColor: "#1F2937",
      color: "#FFFFFF",
      borderColor: "#1F2937",
    },
    cardGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
      gap: "24px",
      marginTop: "20px",
    },
    card: {
      backgroundColor: "#FFFFFF",
      borderRadius: "24px",
      border: "1px solid #E5E7EB",
      padding: "24px",
      minHeight: "170px",
      boxShadow: "0 18px 50px rgba(15, 23, 42, 0.05)",
    },
    cardHeader: {
      display: "flex",
      justifyContent: "space-between",
      gap: "16px",
      flexDirection: "column",
    },
    cardTitle: {
      display: "flex",
      alignItems: "left",
      gap: "12px",
    },
    cardContent: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    },
    assetName: {
      fontSize: "1.15rem",
      fontWeight: 700,
      margin: 0,
    },
    assetSymbol: {
      fontSize: "0.95rem",
      color: "#6B7280",
    },
    networkTag: {
      padding: "6px 12px",
      borderRadius: "999px",
      fontSize: "0.8rem",
      fontWeight: 600,
      backgroundColor: "#EEF2FF",
      color: "#4338CA",
    },
    description: {
      color: "#4B5563",
      lineHeight: 1.7,
      marginTop: "10px",
      fontSize: "0.96rem",
    },
    footerNote: {
      maxWidth: "840px",
      margin: "40px auto 0",
      color: "#6B7280",
      textAlign: "center",
      fontSize: "0.95rem",
      lineHeight: 1.75,
    },
    emptyState: {
      textAlign: "center",
      padding: "40px 20px",
      color: "#6B7280",
      fontSize: "1rem",
    },
  };

  return (
    <div style={styles.page}>
      <Header />
      <main style={styles.main}>
        <section style={styles.hero}>
          <p style={{ textTransform: "uppercase", letterSpacing: "0.22em", color: "#6366F1", fontWeight: 700, marginBottom: "12px" }}>
            Tennet Supported Token/Coin List
          </p>
          <h1 style={styles.title}>Supported coins and tokens by network</h1>
          <p style={styles.subtitle}>
            Explore our supported token and coin list. Search by name, symbol or network to find supported assets.
          </p>
          <p style={{ ...styles.subtitle, marginTop: "18px", fontWeight: 600 }}>
            Showing {assetsCount} supported asset{assetsCount === 1 ? "" : "s"}.
          </p>
        </section>

        <section style={styles.searchBar}>
          <input
            type="search"
            aria-label="Search supported coins"
            placeholder="Search coin, token or network..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            style={styles.input}
          />
        </section>

        <section style={styles.chips}>
          {networkOptions.map((network) => {
            const active = activeNetwork === network;
            return (
              <button
                key={network}
                type="button"
                onClick={() => setActiveNetwork(network)}
                style={{
                  ...styles.chip,
                  ...(active ? styles.chipActive : {}),
                }}
              >
                {network}
              </button>
            );
          })}
        </section>

        <section style={styles.cardGrid}>
          {filteredAssets.length === 0 ? (
            <div style={styles.emptyState}>
              No supported coins match your search. Try another name, symbol or network.
            </div>
          ) : (
            filteredAssets.map((asset) => {
              const description = asset.network === "Other"
                ? "Supported asset."
                : `Supported asset on ${asset.network}.`;

              return (
                <article key={`${asset.network}-${asset.symbol}-${asset.id}`} style={styles.card}>
                  <div style={styles.cardHeader}>
                    <div style={styles.cardTitle}>
                        <span style={styles.networkTag}>{asset.network}</span>
                    </div>
                    <div style={styles.cardContent}>
                      <p style={styles.assetName}>{asset.name}</p>
                      <p style={styles.assetSymbol}>{asset.symbol}</p>
                    </div>
                  </div>
                </article>
              );
            })
          )}
        </section>

        <p style={styles.footerNote}>
          This list is built to reflect our current supported assets across major blockchain networks and the OJK token/coin crypto guidance. Supported assets may change over time as regulatory and market conditions evolve.
        </p>
      </main>
      <Footer />
    </div>
  );
};

export default IndexCryptoList;
